import React, { useState, useEffect, useRef, memo } from 'react';
import { ImageIcon } from 'lucide-react';
import { getOptimizedImageUrl, generateSrcSet, getImageSizes, getPlaceholderUrl, type ImageSize } from '@/utils/imageOptimization';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  priority?: boolean;
  size?: ImageSize;
  variant?: 'card' | 'hero' | 'thumbnail';
  enableBlurUp?: boolean;
}

const OptimizedImage: React.FC<OptimizedImageProps> = memo(({ 
  src, 
  alt, 
  className = '',
  fallbackSrc,
  priority = false,
  size = 'medium',
  variant = 'card',
  enableBlurUp = true
}) => {
  const optimizedSrc = getOptimizedImageUrl(src, size);
  const placeholderSrc = enableBlurUp ? getPlaceholderUrl(src) : null;
  const srcSet = generateSrcSet(src);
  const sizes = getImageSizes(variant);
  
  const [currentSrc, setCurrentSrc] = useState(optimizedSrc);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || isInView) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, isInView]);

  // Reset states when src changes
  useEffect(() => {
    setCurrentSrc(getOptimizedImageUrl(src, size));
    setHasError(false);
    setIsLoaded(false);
  }, [src, size]);

  const handleError = () => {
    if (!hasError && fallbackSrc && currentSrc !== fallbackSrc) {
      setHasError(true);
      setCurrentSrc(fallbackSrc);
    }
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  if (hasError && (!fallbackSrc || currentSrc === fallbackSrc)) {
    return (
      <div className={cn('bg-muted flex items-center justify-center', className)}>
        <ImageIcon className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div ref={imgRef} className={cn('relative overflow-hidden', className)}>
      {/* Blur-up placeholder */}
      {enableBlurUp && placeholderSrc && !isLoaded && (
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg"
        />
      )}
      
      {/* Main image - only load when in view */}
      {isInView && (
        <img
          src={currentSrc}
          srcSet={srcSet || undefined}
          sizes={sizes}
          alt={alt}
          onError={handleError}
          onLoad={handleLoad}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
      
      {/* Loading skeleton when not in view */}
      {!isInView && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;